import { Divider, FormControlLabel, MenuItem, Stack, Switch, TextField } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { CaptionText } from '../../shared/typography/PrimaryTypography';
import { EsgRecordStatus, FileListItem } from '../../../types/syntiro_honey';
import MetricAutocomplete from './MetricAutocomplete';
import EntityFilterSelect from './EntityFilterSelect';
import PeriodSelect from './PeriodSelect';
import { ReviewFilters } from './useRecordList';
import { formatSkippedSheets } from './value_format';

/** Fine-grained statuses. APPROVED is absent: the scope control owns it. */
const STATUS_OPTIONS: Array<{ value: EsgRecordStatus; label: string }> = [
  { value: 'PENDING_REVIEW', label: 'Pending review' },
  { value: 'NEEDS_DISAMBIGUATION', label: 'Disambiguate' },
  { value: 'FLAGGED', label: 'Flagged' },
  { value: 'REJECTED', label: 'Rejected' }
];

interface ReviewFilterPanelProps {
  /** The committed filters. */
  filters: ReviewFilters;
  /** The caller's recent uploads, for the file filter. */
  files: Array<FileListItem>;
  /**
   * The name already known for `filters.metricId`, so a filter set before this
   * popover was opened shows the metric name on first paint rather than its id.
   */
  metricName?: string;
  /** The name already known for `filters.entityId`, for the same reason. */
  entityName?: string;
  /** Called with the full next filter set on any change. */
  onFiltersChange: (filters: ReviewFilters) => void;
  /**
   * Reports the display names of the id-valued selections, so the active-filter row
   * can name them instead of showing a raw uuid.
   */
  onLabelChange: (labels: {
    metricName?: string;
    periodLabel?: string;
    entityName?: string;
  }) => void;
}

/**
 * The secondary filters, grouped into the two questions a reviewer is actually
 * asking. Lives in a popover behind the Filters button.
 *
 * The grouping IS the point. These were previously six controls and two switches in
 * one flat row with uniform spacing, which gave the eye no signal about what related
 * to what — equal gaps read as equal relatedness. Splitting them into "Which
 * records" (identity: metric, period, file) and "What state" (progress: status,
 * follow-up), each under its own heading and divider, lets proximity and common
 * region carry the structure instead of leaving it implicit in source order.
 * @param {ReviewFilterPanelProps} props The filters, file list and change handlers.
 * @returns {JSX.Element}
 */
export default function ReviewFilterPanel({
  filters,
  files,
  metricName,
  entityName,
  onFiltersChange,
  onLabelChange
}: ReviewFilterPanelProps) {
  return (
    <Stack spacing={2} sx={{ p: 2, minWidth: 320 }}>
      <Stack spacing={1.5}>
        <CaptionText weight="Medium">Which records</CaptionText>
        <MetricAutocomplete
          label="Metric"
          selectedId={filters.metricId ?? null}
          selectedLabel={metricName}
          onSelect={(metric) => {
            onLabelChange({ metricName: metric?.name });
            onFiltersChange({ ...filters, metricId: metric?.id });
          }}
          // The popover unmounts on close, so a filter set earlier arrives here as
          // a bare id; report the resolved name so the active-filter chip agrees.
          onResolveLabel={(metric) => onLabelChange({ metricName: metric.name })}
        />
        <EntityFilterSelect
          label="Entity"
          value={filters.entityId ?? null}
          currentLabel={entityName}
          onChange={(entity) => {
            onLabelChange({ entityName: entity?.name });
            onFiltersChange({
              ...filters,
              entityId: entity?.entityId,
              entityLevel: entity?.entityLevel
            });
          }}
        />
        <PeriodSelect
          label="Period"
          value={filters.periodId ?? null}
          onChange={(periodId, periodLabel) => {
            onLabelChange({ periodLabel });
            onFiltersChange({ ...filters, periodId: periodId ?? undefined });
          }}
          allowEmpty
        />
        <TextField
          select
          label="File"
          size="small"
          value={filters.fileId ?? ''}
          onChange={(event) =>
            onFiltersChange({
              ...filters,
              fileId: event.target.value === '' ? undefined : event.target.value
            })
          }
          fullWidth
        >
          <MenuItem value="">All files</MenuItem>
          {files.map((file) => (
            <MenuItem key={file.id} value={file.id}>
              {/* Time, not just date: the same workbook is often uploaded more than
                  once in a day, and a date-only label makes those indistinguishable
                  (Maxine 2026-08-03 ④). */}
              {`${file.filename} (${new Date(file.createdAt).toLocaleString()})`}
              {/* Coverage honesty follows the file everywhere it appears: this file
                  has sheets extraction could not read (persisted server-side). The
                  summary is the icon's ACCESSIBLE NAME (titleAccess), not a tooltip:
                  inside an open Select a tooltip is unreachable by construction, but
                  the name joins the option's text, so assistive tech reads it. */}
              {(file.skippedSheets ?? []).length > 0 && (
                <WarningAmberIcon
                  color="warning"
                  sx={{ fontSize: 16, ml: 0.5 }}
                  titleAccess={`— skipped sheets: ${formatSkippedSheets(file.skippedSheets ?? [])}`}
                />
              )}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Divider />
      <Stack spacing={1.5}>
        <CaptionText weight="Medium">What state</CaptionText>
        <TextField
          select
          label="Status"
          size="small"
          value={filters.status === 'APPROVED' ? '' : (filters.status ?? '')}
          onChange={(event) => {
            const next = event.target.value as EsgRecordStatus | '';
            onFiltersChange({ ...filters, status: next === '' ? undefined : next });
          }}
          fullWidth
        >
          <MenuItem value="">Any status</MenuItem>
          {STATUS_OPTIONS.map((status) => (
            <MenuItem key={status.value} value={status.value}>
              {status.label}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={
            <Switch
              checked={filters.needsFollowUp === true}
              onChange={(event) =>
                onFiltersChange({
                  ...filters,
                  needsFollowUp: event.target.checked ? true : undefined
                })
              }
            />
          }
          label="Needs follow-up"
        />
      </Stack>
    </Stack>
  );
}
